import { useState, useEffect, useRef } from 'react';
import dashboardData from '../../../../data/dashboard/dashboardData.json';
import SeeItWorkingCard from './sections/SeeItWorkingCard';
import ShowcaseCountersCard from './sections/ShowcaseCountersCard';
import CoveredDestinationsCard from './sections/CoveredDestinationsCard';
import CoveredDestinationsModal from './sections/CoveredDestinationsModal';

const coveredDestinations = dashboardData.coveredDestinations || {};
const domesticDestinations = coveredDestinations.domestic || [];
const internationalDestinations = coveredDestinations.international || [];
const totalDestinationsCount = domesticDestinations.length + internationalDestinations.length;

const FEATURED_DOMESTIC = coveredDestinations.featuredDomestic || [];
const FEATURED_INTERNATIONAL = coveredDestinations.featuredInternational || [];

// Animated Count-Up Hook on Scroll
const useCountUp = (endValue, duration = 1600, shouldStart = false) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!shouldStart) return;

    let startTime = null;
    let animationFrame = null;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      // Ease out quad
      const easeOut = 1 - (1 - progress) * (1 - progress);
      setCount(Math.floor(easeOut * endValue));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        setCount(endValue);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [endValue, duration, shouldStart]);

  return count;
};

const DashboardShowcase = () => {
  const [destinationTab, setDestinationTab] = useState('domestic');
  const [isDestinationsModalOpen, setIsDestinationsModalOpen] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const showcaseRef = useRef(null);

  // Intersection Observer for scroll-triggered counter
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
        }
      },
      { threshold: 0.15 }
    );

    if (showcaseRef.current) {
      observer.observe(showcaseRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Animated counters
  const agentsCount = useCountUp(1200, 1600, isInView);
  const destinationsCount = useCountUp(totalDestinationsCount, 1400, isInView);
  const partnersCount = useCountUp(6500, 1800, isInView);

  const activeLocations =
    destinationTab === 'domestic' ? domesticDestinations : internationalDestinations;
  const featuredLocations =
    destinationTab === 'domestic' ? FEATURED_DOMESTIC : FEATURED_INTERNATIONAL;

  return (
    <div ref={showcaseRef} className="w-full">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 xl:gap-5 items-stretch">
        {/* Left Card: See it working video */}
        <SeeItWorkingCard />

        {/* Right Column: Stats & Where Travellers are Covered */}
        <div className="lg:col-span-5 xl:col-span-5 flex flex-col gap-4 xl:gap-5">
          {/* Top Right Card: Animated Counters */}
          <ShowcaseCountersCard
            agentsCount={agentsCount}
            destinationsCount={destinationsCount}
            partnersCount={partnersCount}
          />

          {/* Bottom Right Card: Where your travellers are covered */}
          <CoveredDestinationsCard
            destinationTab={destinationTab}
            setDestinationTab={setDestinationTab}
            featuredLocations={featuredLocations}
            totalActiveLocationsCount={activeLocations.length}
            onOpenModal={() => setIsDestinationsModalOpen(true)}
          />
        </div>
      </div>

      {/* Full Covered Destinations Directory Modal */}
      <CoveredDestinationsModal
        isOpen={isDestinationsModalOpen}
        onClose={() => setIsDestinationsModalOpen(false)}
        destinationTab={destinationTab}
        setDestinationTab={setDestinationTab}
        domesticDestinations={domesticDestinations}
        internationalDestinations={internationalDestinations}
        totalDestinationsCount={totalDestinationsCount}
      />
    </div>
  );
};

export default DashboardShowcase;
